import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Pencil, X, Check, Sparkles, Loader2 } from "lucide-react";
import { ALL_CATEGORIES, type GalleryCategory, type GalleryImageData } from "@/data/galleryImages";
import { findSectionUsages } from "@/data/sectionImageUsage";

export interface DbGalleryImage extends GalleryImageData {
  id: string;
  sort_order: number;
}

interface Props {
  image: GalleryImageData | DbGalleryImage;
  onDeleted: () => void;
  onUpdated: () => void;
}

const ImageCard = ({ image, onDeleted, onUpdated }: Props) => {
  const isDb = "id" in image && !image.isHardcoded;
  const [editing, setEditing] = useState(false);
  const [editAlt, setEditAlt] = useState(image.alt);
  const [editCategories, setEditCategories] = useState<GalleryCategory[]>(image.categories);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const handleSuggestAlt = async () => {
    setSuggesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-alt-text", {
        body: { imageUrl: image.src },
      });
      if (error) throw error;
      if (data?.altText) {
        setEditAlt(data.altText);
        toast.success("AI suggestion applied");
      } else if (data?.error) {
        toast.error(data.error);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to get suggestion");
    } finally {
      setSuggesting(false);
    }
  };

  const toggleCat = (cat: GalleryCategory) => {
    setEditCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleSave = async () => {
    if (editCategories.length === 0) {
      toast.error("Select at least one category");
      return;
    }
    setSaving(true);

    if (isDb) {
      // Update existing DB record
      const { error } = await supabase
        .from("gallery_images")
        .update({ alt: editAlt, categories: editCategories })
        .eq("id", (image as DbGalleryImage).id);
      setSaving(false);
      if (error) {
        toast.error("Failed to update");
      } else {
        toast.success("Image updated");
        setEditing(false);
        onUpdated();
      }
    } else {
      // Hardcoded image — insert into DB so edits are persisted
      const { error } = await supabase
        .from("gallery_images")
        .insert({
          src: image.src,
          alt: editAlt,
          categories: editCategories,
          sort_order: 0,
        });
      setSaving(false);
      if (error) {
        toast.error("Failed to save changes");
      } else {
        toast.success("Image saved to database — now fully editable");
        setEditing(false);
        onUpdated();
      }
    }
  };

  const handleDelete = async () => {
    const sectionUsages = findSectionUsages(image.src);
    let confirmMsg = isDb
      ? "Permanently delete this image? This cannot be undone."
      : "Permanently remove this built-in image from the website? This cannot be undone from the admin panel.";

    if (sectionUsages.length > 0) {
      confirmMsg =
        `⚠️ WARNING: This image is also used as a section image on the website:\n\n` +
        sectionUsages.map((l) => `  • ${l}`).join("\n") +
        `\n\nDeleting it here will only remove it from the gallery. The section(s) above will show a broken image until a developer replaces it.\n\n` +
        `Are you sure you want to delete it?`;
    }

    const ok = window.confirm(confirmMsg);
    if (!ok) return;

    if (!isDb) {
      // Hardcoded image — record as hidden so it's removed from gallery everywhere
      const { error } = await supabase
        .from("hidden_gallery_sources")
        .insert({ src: image.src });
      if (error) {
        toast.error("Failed to delete image");
        return;
      }
      toast.success("Image deleted");
      onDeleted();
      return;
    }

    const dbImg = image as DbGalleryImage;
    const parts = dbImg.src.split("/");
    const fileName = parts[parts.length - 1];
    if (dbImg.src.includes("supabase")) {
      await supabase.storage.from("gallery").remove([fileName]);
    }
    const { error } = await supabase.from("gallery_images").delete().eq("id", dbImg.id);
    if (error) {
      toast.error("Failed to delete image");
      return;
    }
    toast.success("Image deleted");
    onDeleted();
  };


  const startEdit = () => {
    setEditAlt(image.alt);
    setEditCategories(image.categories);
    setEditing(true);
  };

  return (
    <div className="border border-border overflow-hidden group relative">
      <img src={image.src} alt={image.alt} className="w-full aspect-square object-cover" />

      {editing ? (
        <div className="p-3 space-y-3 bg-background">
          <div>
            <Label className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Description / Alt Text</Label>
            <div className="flex gap-1 mt-1">
              <Input
                value={editAlt}
                onChange={(e) => setEditAlt(e.target.value)}
                className="text-sm h-8"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleSuggestAlt}
                disabled={suggesting}
                className="h-8 px-2 shrink-0"
                title="AI suggest alt text"
              >
                {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1 block">Categories</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_CATEGORIES.map((cat) => (
                <label key={cat} className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={editCategories.includes(cat)}
                    onCheckedChange={() => toggleCat(cat)}
                  />
                  <span className="text-xs text-foreground">{cat}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs">
              <Check className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-7 text-xs">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-2">
          <p className="text-xs text-muted-foreground truncate">{image.alt}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {image.categories.map((cat) => (
              <span key={cat} className="text-[10px] tracking-wider uppercase bg-secondary text-secondary-foreground px-2 py-0.5">
                {cat}
              </span>
            ))}
          </div>
          <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 mt-1 inline-block ${
            image.isHardcoded ? "bg-muted text-muted-foreground" : "bg-secondary text-secondary-foreground"
          }`}>
            {image.isHardcoded ? "Built-in" : "Uploaded"}
          </span>
        </div>
      )}

      {!editing && (
        <div className="absolute top-2 right-2 flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button onClick={startEdit} className="bg-primary text-primary-foreground p-1.5" title="Edit">
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground p-1.5"
            title="Delete image"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

    </div>
  );
};

export default ImageCard;
